import { AppIconsType } from '@fc-shared/ui/icon/icons';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';

// // --- Entity types returned by the quick search API ---

export type QuickSearchEntityType =
  | 'vehicle_vehicle'
  | 'authentication_employee'
  | 'poi_location'
  | 'task_task'
  | 'task_workflow'
  | 'chat_channel'
  | 'crm_company'
  | 'crm_contact';

// --- Tab filter type (includes virtual 'all' and 'crm' types) ---

export type QuickSearchType =
  | 'all'
  | 'vehicle_vehicle'
  | 'authentication_employee'
  | 'poi_location'
  | 'crm'
  | 'chat_channel'
  | 'task';

export interface QuickSearchTypeTab {
  id: QuickSearchType;
  label?: string;
  icon?: AppIconsType;
}

export const QUICK_SEARCH_TABS: QuickSearchTypeTab[] = [
  { id: 'all', label: 'All' },
  { id: 'vehicle_vehicle', icon: 'icon-vehicles-bold' },
  { id: 'authentication_employee', icon: 'icon-group-bold' },
  { id: 'poi_location', icon: 'icon-location-bold' },
  { id: 'task', icon: 'icon-tasks-bold' },
  { id: 'chat_channel', icon: 'icon-chats-bold' },
  { id: 'crm', icon: 'icon-crm-bold' },
];

// --- Quick search API obj types per entity ---

export interface QuickSearchVehicleObj {
  id: string;
  identifier: string;
  type: string;
  groupName: string[];
  vin: string;
  customerId: string;
  manufacturer: string;
  model: string;
  status: string | null;
  visibility: number;
  photo: AttachmentModel | null;
}

export interface QuickSearchEmployeeObj {
  id: string;
  customerId: string;
  email: string;
  groupName: string[];
  primaryPhoneNumber: string;
  secondaryPhoneNumber: string;
  firstName: string;
  lastName: string;
  fullName: string;
  color: string;
  avatar: AttachmentModel | null;
}

export interface QuickSearchLocationObj {
  id: string;
  name: string;
  company: string | null;
  type: {
    id: string;
    name: string;
    color: string;
    icon: AppIconsType;
  };
  photo: AttachmentModel | null;
}

export interface QuickSearchTaskAssignee {
  id: string;
  firstName: string;
  lastName: string;
  color: string;
  avatar: AttachmentModel | null;
}

export interface QuickSearchTaskObj {
  id: string;
  name: string;
  displayNumber: string;
  status: { id: string };
  workflow: { id: string; name: string };
  dueDate: string | null;
  dueTime: string | null;
  assignees: QuickSearchTaskAssignee[];
}

export interface QuickSearchChannelObj {
  id: string;
  name: string;
  type: string;
  fcOwnerId: string;
  members: string[];
  memberNames: string[];
}

export interface QuickSearchCompanyObj {
  id: number;
  name: string;
  emails: string[];
  phoneNumbers: string[];
  color: string;
  photo: AttachmentModel | null;
}

export interface QuickSearchContactObj {
  id: number;
  company: string | null;
  emails: string[];
  phoneNumbers: string[];
  color: string;
  photo: AttachmentModel | null;
}

// --- Discriminated union for API items ---

interface BaseQuickSearchApiItem<
  T extends QuickSearchEntityType,
  O,
  S = Record<string, unknown>,
> {
  entityType: T;
  obj: O;
  rankingScore?: number;
  searchData?: S;
}

export type QuickSearchVehicleItem = BaseQuickSearchApiItem<
  'vehicle_vehicle',
  QuickSearchVehicleObj
>;
export type QuickSearchEmployeeItem = BaseQuickSearchApiItem<
  'authentication_employee',
  QuickSearchEmployeeObj
>;
export type QuickSearchLocationItem = BaseQuickSearchApiItem<
  'poi_location',
  QuickSearchLocationObj
>;
export type QuickSearchTaskItem = BaseQuickSearchApiItem<
  'task_task',
  QuickSearchTaskObj
>;
export type QuickSearchWorkflowItem = BaseQuickSearchApiItem<
  'task_workflow',
  {
    id: string;
    name: string;
    description: string;
  }
>;
export type QuickSearchChannelItem = BaseQuickSearchApiItem<
  'chat_channel',
  QuickSearchChannelObj
>;
export type QuickSearchCompanyItem = BaseQuickSearchApiItem<
  'crm_company',
  QuickSearchCompanyObj,
  { contactCount?: number }
>;
export type QuickSearchContactItem = BaseQuickSearchApiItem<
  'crm_contact',
  QuickSearchContactObj,
  { name?: string }
>;

export type QuickSearchApiItem =
  | QuickSearchVehicleItem
  | QuickSearchEmployeeItem
  | QuickSearchLocationItem
  | QuickSearchTaskItem
  | QuickSearchWorkflowItem
  | QuickSearchChannelItem
  | QuickSearchCompanyItem
  | QuickSearchContactItem;
